import React, { lazy, Suspense } from "react";
import { isEmpty } from "lodash";

import {
    formatArticles,
    getKnowledgeCenterLanguageUrl,
} from "~/utilities/format";

import Skeleton from "~/components/loaders/Skeleton";

import useGetBasePath from "~/hooks/useGetBasePath";
import { useTranslationContext } from "~/context/TranslationContext";
import useStringTranslation from "~/hooks/translations/useStringTranslation";

const KnowledgeCenter = lazy(() => import("~/blocks/KnowledgeCenter"));

const getRelatedArticles = (
    related,
    sameLanguagePrimaryTopicArticles,
    primaryTopicArticles,
    getBasePath,
) => {
    // English language articles that have the same Primary Topic
    let articles = primaryTopicArticles;

    // Articles with the same language and the same Primary Topic
    if (sameLanguagePrimaryTopicArticles?.length) {
        articles = sameLanguagePrimaryTopicArticles;
    }

    // Articles that were selected at the article level to show in the More Articles section
    if (related.length) {
        articles = related;
    }

    return formatArticles(articles, -1, getBasePath);
};

const ArticleKnowledgeCenter = ({ data }) => {
    const {
        allWpArticle: { nodes: primaryTopicArticles },
        sameLanguageArticles: { nodes: sameLanguagePrimaryTopicArticles },
        wpArticle: { details },
    } = data;

    const getBasePath = useGetBasePath();

    const { activeCode } = useTranslationContext();

    const { viewAllText, moreArticlesText } = useStringTranslation([
        { text: "More Articles", handle: "moreArticlesText" },
        { text: "View All", handle: "viewAllText" },
    ]);

    const relatedArticles = getRelatedArticles(
        details.relatedKnowledgeCenterArticles || [],
        sameLanguagePrimaryTopicArticles,
        primaryTopicArticles,
        getBasePath,
    );

    if (isEmpty(relatedArticles)) {
        return null;
    }

    return (
        <Suspense fallback={<Skeleton variant="block" />}>
            <KnowledgeCenter
                title={moreArticlesText}
                linkLabel={viewAllText}
                linkUrl={getKnowledgeCenterLanguageUrl(activeCode, getBasePath)}
                cards={relatedArticles}
                withBorder
            />
        </Suspense>
    );
};

export default ArticleKnowledgeCenter;
